const TermsPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-20">
        <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-12 shadow-sm">
          <h1 className="text-4xl md:text-5xl font-bold text-[#003B5C] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Terms & Conditions
          </h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-slate-600 leading-relaxed mb-6">
              <strong>Last Updated:</strong> February 7, 2026
            </p>

            <p className="text-slate-700 leading-relaxed mb-6">
              By subscribing to or using the Lucent Sky ESG platform, you agree to these Terms and Conditions.
              Please read them carefully before creating an account.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Subscription & Access
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Access to the Lucent Sky ESG platform is provided on a subscription basis. Subscriptions are billed monthly or annually in advance.
              You may cancel your subscription at any time through your dashboard billing settings.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-6 mb-6">
              <p className="text-blue-900 mb-2"><strong>Platform Access:</strong></p>
              <p className="text-blue-800">
                The Emissions Tracker Dashboard is a digital tool provided via our secure cloud platform.
                If you have questions about whether the platform is right for your business, we recommend reviewing our
                FAQ or contacting us at hello@lucentsky.com.au.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Standardized Legal Disclaimers
            </h2>

            <div className="space-y-4 mb-8">
              {/* No-Advice Disclaimer */}
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-4">
                <p className="text-blue-900 font-semibold mb-2">1. No-Advice Disclaimer</p>
                <p className="text-blue-800 text-sm">
                  Lucent Sky ESG provides a data calculation service only. This report does not constitute financial, legal, or climate-related advisory services.
                </p>
              </div>

              {/* Greenwashing Protection */}
              <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded p-4">
                <p className="text-emerald-900 font-semibold mb-2">2. Greenwashing Protection</p>
                <p className="text-emerald-800 text-sm">
                  This report is based on user-provided data. Lucent Sky ESG does not warrant the authenticity of source documents provided by the user.
                </p>
              </div>

              {/* NGA Factor Liability */}
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-4">
                <p className="text-amber-900 font-semibold mb-2">3. NGA Factor Liability</p>
                <p className="text-amber-800 text-sm">
                  Calculations are based on the 2026 NGA Workbook. Lucent Sky ESG is not liable for changes or errors in government-published emission factors.
                </p>
              </div>

              {/* Methodology Disclosure */}
              <div className="bg-purple-50 border-l-4 border-purple-500 rounded p-4">
                <p className="text-purple-900 font-semibold mb-2">4. Methodology Disclosure</p>
                <p className="text-purple-800 text-sm">
                  Scope 2 emissions are calculated using the "Location-Based" method as per AASB S2 requirements.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Disclaimer of Warranties
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              <strong>Lucent Sky ESG provides informational tools, not financial or legal advice.</strong> Our platform
              and resources are designed to assist Australian SMEs with carbon reporting and sustainability tracking,
              but they do not constitute:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>Professional financial advice</li>
              <li>Legal counsel or regulatory compliance guarantees</li>
              <li>Accounting or tax preparation services</li>
              <li>Grant application preparation or submission services</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              User Responsibilities
            </h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              By using our products, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li><strong>Verification Required:</strong> You are responsible for verifying all calculations and data outputs from the platform</li>
              <li><strong>Professional Consultation:</strong> You should consult qualified professionals (accountants, lawyers, sustainability consultants) before making business decisions</li>
              <li><strong>Compliance:</strong> You remain responsible for ensuring compliance with all relevant regulations and reporting requirements</li>
              <li><strong>Due Diligence:</strong> Proper due diligence is required for your specific circumstances and industry</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Grant Information Disclaimer
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              The grant eligibility tool and information provided are for educational purposes only. Grant eligibility,
              funding amounts, and program availability are:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>Subject to change by government bodies without notice</li>
              <li>Dependent on meeting specific criteria set by grant administrators</li>
              <li>Competitive and not guaranteed even if you meet eligibility requirements</li>
              <li>Subject to budget availability and program timelines</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-6">
              <strong>Lucent Sky ESG makes no guarantees regarding grant approvals or contract awards.</strong> We provide
              information tools to help you identify potential opportunities, but application success depends on many factors
              beyond our control.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Limitation of Liability
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              To the maximum extent permitted by Australian law, Lucent Sky ESG shall not be liable for:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>Any indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, revenue, or business opportunities</li>
              <li>Grant application rejections or contract award failures</li>
              <li>Errors or omissions in grant information or ESG calculations</li>
              <li>Reliance on information provided without independent verification</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mb-6">
              Our total liability for any claim arising from use of our products shall not exceed the amount you paid for the product.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Intellectual Property
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              The Lucent Sky ESG Platform, including all formulas, templates, and content, is protected by copyright.
              Upon purchase, you receive a <strong>single-user license</strong> for your business use. You may not:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li>Resell, redistribute, or share the tracker with other businesses</li>
              <li>Claim authorship or remove attribution</li>
              <li>Reverse engineer proprietary formulas for commercial purposes</li>
            </ul>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Updates and Support
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Lifetime updates are included with your active subscription. We'll deploy platform updates automatically.
              Basic email support is provided for Australian businesses through hello@lucentsky.com.au with typical response
              times within 24 hours.
            </p>

            <h2 id="methodology" className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Calculation Methodology
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              Lucent Sky ESG uses the following methodology for carbon emission calculations:
            </p>
            <ul className="list-disc pl-6 mb-6 text-slate-700 space-y-2">
              <li><strong>Framework Alignment:</strong> GHG Protocol Corporate Standard and AASB S2 Climate-related Disclosures</li>
              <li><strong>Scope 2 Method:</strong> Location-Based approach using state-specific grid factors</li>
              <li><strong>Emission Factors:</strong> 2026 National Greenhouse Accounts (NGA) Workbook published by the Australian Government</li>
              <li><strong>Factor Versioning:</strong> Each emission entry stores the factor value used at creation time to ensure audit accuracy</li>
              <li><strong>Unit of Measurement:</strong> All emissions expressed in tonnes of carbon dioxide equivalent (tCO₂-e)</li>
            </ul>

            <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-6 mb-6">
              <p className="text-blue-900 mb-2"><strong>State Electricity Grid Factors (2026 NGA):</strong></p>
              <div className="grid grid-cols-2 gap-2 text-blue-800 text-sm">
                <p>• NSW/ACT: 0.64 kg CO₂-e/kWh</p>
                <p>• Victoria: 0.82 kg CO₂-e/kWh</p>
                <p>• Queensland: 0.67 kg CO₂-e/kWh</p>
                <p>• South Australia: 0.25 kg CO₂-e/kWh</p>
                <p>• Western Australia: 0.51 kg CO₂-e/kWh</p>
                <p>• Tasmania: 0.15 kg CO₂-e/kWh</p>
                <p>• Northern Territory: 0.54 kg CO₂-e/kWh</p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Governing Law
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              These Terms and Conditions are governed by the laws of Queensland, Australia. Any disputes shall be subject
              to the exclusive jurisdiction of the courts of Queensland.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Changes to Terms
            </h2>
            <p className="text-slate-700 leading-relaxed mb-6">
              We may update these Terms from time to time. Material changes will be communicated via email. Continued use
              of our services after changes constitutes acceptance of the updated Terms.
            </p>

            <h2 className="text-2xl font-semibold text-[#003B5C] mt-8 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Contact Information
            </h2>
            <div className="bg-[#EDF6F9] border-l-4 border-[#74A57F] rounded p-6 mb-6">
              <p className="text-slate-700 mb-2"><strong>Email:</strong> hello@lucentsky.com.au</p>
              <p className="text-slate-700 mb-2"><strong>Location:</strong> Doonan, Sunshine Coast, Queensland</p>
              <p className="text-slate-700"><strong>ABN:</strong> 12 345 678 901</p>
            </div>

            <div className="bg-[#003B5C] rounded-lg p-6 mt-8">
              <p className="text-white font-semibold mb-2">By using the platform, you confirm that:</p>
              <ul className="list-disc pl-6 text-white/90 space-y-1 text-sm">
                <li>You have read and understood these Terms and Conditions</li>
                <li>You understand this is a subscription-based service</li>
                <li>You will seek professional advice for specific business decisions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;